import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import AdminLayout from '../components/AdminLayout'
import {
  ClipboardList, X, CheckCircle, AlertCircle, Loader,
  Search, Filter, ChevronLeft, ChevronRight, Calendar,
  MapPin, Truck, User, Weight, Clock, Eye, Check,
  XCircle, RefreshCw, Grid, Table, CalendarDays, Zap,
  AlertTriangle, TrendingUp, Users, Car, DollarSign, CalendarCheck, Bell
} from 'lucide-react'
import {
  FetchBookingRequests,
  AdminFindNearestDriver,
  AdminAcceptBooking,
  AdminCancelBooking,
  ResetAdminBookingRequest,
} from '../actions/AdminBookingRequestAction'

// ─── localStorage helpers for persistent timer & accepted state ──────────────
const LS_SEEN_KEY     = 'adminBooking_seenAt'
const LS_ACCEPTED_KEY = 'adminBooking_acceptedIds'

const loadSeenAt = () => {
  try { return JSON.parse(localStorage.getItem(LS_SEEN_KEY) || '{}') } catch { return {} }
}
const saveSeenAt = (map) => {
  try { localStorage.setItem(LS_SEEN_KEY, JSON.stringify(map)) } catch {}
}
const loadAcceptedIds = () => {
  try { return new Set(JSON.parse(localStorage.getItem(LS_ACCEPTED_KEY) || '[]')) } catch { return new Set() }
}
const saveAcceptedIds = (set) => {
  try { localStorage.setItem(LS_ACCEPTED_KEY, JSON.stringify([...set])) } catch {}
}

// ─── Helper Functions (defined first) ────────────────────────────────────────
const parseBookingTime = (val) => {
  if (!val) return null
  const d = new Date(val)
  return isNaN(d.getTime()) ? null : d
}

const doTimesOverlap = (a, b) => {
  const aStart = parseBookingTime(a.bsTime)
  const aEnd   = parseBookingTime(a.beTime)
  const bStart = parseBookingTime(b.bsTime)
  const bEnd   = parseBookingTime(b.beTime)
  if (!aStart || !aEnd || !bStart || !bEnd) return false
  return aStart < bEnd && bStart < aEnd
}

const normalizeTonnage = (t) =>
  String(t || '').toLowerCase().replace(/[^0-9.]/g, '')

const getConflictingReqIds = (bookings) => {
  const conflicts = new Set()
  for (let i = 0; i < bookings.length; i++) {
    for (let j = i + 1; j < bookings.length; j++) {
      const a = bookings[i]
      const b = bookings[j]
      if (normalizeTonnage(a.tonnage) === normalizeTonnage(b.tonnage) && doTimesOverlap(a, b)) {
        conflicts.add(String(a.reqId || a.bId))
        conflicts.add(String(b.reqId || b.bId))
      }
    }
  }
  return conflicts
}

const isTimeSlotExpired = (booking) => {
  if (!booking || !booking.beTime) return false
  const endTime = new Date(booking.beTime).getTime()
  const now = Date.now()
  return now > endTime
}

const getTimeSlotRemaining = (booking) => {
  if (!booking || !booking.beTime) return null
  const endTime = new Date(booking.beTime).getTime()
  const now = Date.now()
  return Math.max(0, endTime - now)
}

const normalizeDate = (dt) => {
  if (!dt) return ''
  const d = new Date(dt)
  return isNaN(d) ? '' : d.toISOString().split('T')[0]
}

// ─── Countdown Timer Display ─────────────────────────────────────────────────
function CountdownBadge({ seenAt, timeSlotEndTime }) {
  const [remaining, setRemaining] = useState(null)
  const [timeSlotRemaining, setTimeSlotRemaining] = useState(null)

  useEffect(() => {
    if (!seenAt) return
    
    const calc = () => {
      const elapsed = Date.now() - seenAt
      const rem = Math.max(0, 3 * 60 * 1000 - elapsed)
      setRemaining(rem)
    }
    
    calc()
    const id = setInterval(calc, 500)
    return () => clearInterval(id)
  }, [seenAt])

  useEffect(() => {
    if (!timeSlotEndTime) return
    
    const calcTimeSlot = () => {
      const endTime = new Date(timeSlotEndTime).getTime()
      const now = Date.now()
      const rem = Math.max(0, endTime - now)
      setTimeSlotRemaining(rem)
    }
    
    calcTimeSlot()
    const id = setInterval(calcTimeSlot, 1000)
    return () => clearInterval(id)
  }, [timeSlotEndTime])

  if (remaining === null && timeSlotRemaining === null) return null

  const mins = Math.floor((remaining || 0) / 60000)
  const secs = Math.floor(((remaining || 0) % 60000) / 1000)
  const urgent = (remaining || 0) < 60000

  return (
    <div className="flex flex-col gap-1">
      {/* <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
        urgent
          ? 'bg-red-100 text-red-700 animate-pulse'
          : 'bg-orange-100 text-orange-700'
      }`}>
        <Clock size={10} />
        {remaining === 0 ? 'Processing...' : `Auto in ${mins}m ${secs.toString().padStart(2, '0')}s`}
      </span> */}
      {/* {timeSlotRemaining !== null && timeSlotRemaining < 180000 && (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
          timeSlotRemaining < 60000
            ? 'bg-red-200 text-red-800 animate-pulse'
            : 'bg-amber-100 text-amber-700'
        }`}>
          <AlertTriangle size={10} />
          Slot expires in {Math.floor(timeSlotRemaining / 60000)}m {Math.floor((timeSlotRemaining % 60000) / 1000)}s
        </span>
      )} */}
    </div>
  )
}

// ─── Supervisor Cancel Notification Popup ────────────────────────────────────
function CancelNotificationPopup({ notifications, onDismiss }) {
  if (!notifications || notifications.length === 0) return null

  return (
    <div className="fixed top-6 right-6 z-[9999] flex flex-col gap-3 max-w-sm w-full">
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(110%); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
        .cancel-notif { animation: slideInRight 0.3s ease-out; }
      `}</style>
      {notifications.map((n) => (
        <div
          key={n.id}
          className={`cancel-notif bg-white border-l-4 rounded-xl shadow-2xl p-4 ${
            n.cancelType === 'time-slot-expired' 
              ? 'border-l-amber-500'
              : 'border-l-red-500'
          }`}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-start gap-3">
              <div className={`p-2 rounded-lg shrink-0 mt-0.5 ${
                n.cancelType === 'time-slot-expired' ? 'bg-amber-100' : 'bg-red-100'
              }`}>
                {n.cancelType === 'time-slot-expired' 
                  ? <AlertTriangle size={18} className="text-amber-600" />
                  : <XCircle size={18} className="text-red-600" />
                }
              </div>
              <div>
                <p className="text-sm font-bold text-gray-900 mb-1">
                  {n.cancelType === 'time-slot-expired'
                    ? '⏰ Time Slot Expired - Booking Cancelled'
                    : '⚠️ Trip Request Auto-Cancelled'}
                </p>
                <p className="text-xs font-semibold text-red-700 mb-1">
                  Booking #{n.bookingId}
                </p>
                <p className="text-xs text-gray-600 leading-relaxed">
                  {n.reason}
                </p>
                <p className="text-xs text-gray-400 mt-2">{n.time}</p>
              </div>
            </div>
            <button
              onClick={() => onDismiss(n.id)}
              className="p-1 hover:bg-gray-100 rounded-lg transition shrink-0"
            >
              <X size={14} className="text-gray-400" />
            </button>
          </div>
          <button
            onClick={() => onDismiss(n.id)}
            className="mt-3 w-full px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-700 text-xs font-semibold rounded-lg transition"
          >
            Dismiss
          </button>
        </div>
      ))}
    </div>
  )
}

// ─── Stat Card Component ─────────────────────────────────────────────────────
function StatCard({ title, value, icon: Icon, color, subtitle }) {
  const colorClasses = {
    blue:   { bg: 'bg-blue-50',    iconBg: 'bg-blue-100',    text: 'text-blue-600',    border: 'border-blue-200'   },
    purple: { bg: 'bg-purple-50',  iconBg: 'bg-purple-100',  text: 'text-purple-600',  border: 'border-purple-200' },
    indigo: { bg: 'bg-indigo-50',  iconBg: 'bg-indigo-100',  text: 'text-indigo-600',  border: 'border-indigo-200' },
    teal:   { bg: 'bg-teal-50',    iconBg: 'bg-teal-100',    text: 'text-teal-600',    border: 'border-teal-200'   },
    yellow: { bg: 'bg-yellow-50',  iconBg: 'bg-yellow-100',  text: 'text-yellow-600',  border: 'border-yellow-200' },
    green:  { bg: 'bg-emerald-50', iconBg: 'bg-emerald-100', text: 'text-emerald-600', border: 'border-emerald-200'},
    red:    { bg: 'bg-red-50',     iconBg: 'bg-red-100',     text: 'text-red-600',     border: 'border-red-200'    },
  }
  const colors = colorClasses[color] || colorClasses.blue
  return (
    <div className={`${colors.bg} border ${colors.border} rounded-xl p-4 transition-all hover:shadow-md`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`${colors.iconBg} p-3 rounded-xl`}>
          <Icon size={20} className={colors.text} />
        </div>
      </div>
    </div>
  )
}

// ─── Tab Button Component ────────────────────────────────────────────────────
function TabButton({ active, onClick, children, icon: Icon, count, activeColor }) {
  const activeStyle = activeColor === 'teal'
    ? 'bg-white text-teal-700 shadow-sm border border-teal-200'
    : 'bg-white text-gray-900 shadow-sm border border-gray-200'
  const countStyle = activeColor === 'teal'
    ? 'bg-teal-100 text-teal-700'
    : active ? 'bg-gray-100 text-gray-700' : 'bg-gray-100 text-gray-500'

  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium transition-all rounded-lg ${
        active ? activeStyle : 'text-gray-500 hover:text-gray-700 hover:bg-white/50'
      }`}
    >
      {Icon && <Icon size={16} />}
      {children}
      {count !== undefined && (
        <span className={`ml-1.5 px-2 py-0.5 rounded-full text-xs ${countStyle}`}>
          {count}
        </span>
      )}
    </button>
  )
}

// ─── Booking Card Component ─────────────────────────────────────────────────
function BookingCard({ booking, onView, onAccept, onCancel, statusColor, normalizeDate, hasConflict, isBlocked, showActions, showTonnage, seenAt, acceptedReqIds = new Set() }) {
  const cardId = String(booking.reqId || booking.bId)
  const statusClass = statusColor(booking.status)
  return (
    <div className={`bg-white rounded-xl border p-4 hover:shadow-md transition-all ${
      hasConflict
        ? isBlocked ? 'border-red-200 bg-red-50/30' : 'border-yellow-200 bg-yellow-50/30'
        : seenAt
        ? 'border-orange-200 bg-orange-50/20'
        : 'border-gray-100'
    }`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-sm font-semibold text-gray-900">#{booking.reqId || booking.bId}</span>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusClass}`}>
              {booking.status === 'Pending'   && <Clock size={10} />}
              {booking.status === 'Active'    && <CheckCircle size={10} />}
              {booking.status === 'Completed' && <Check size={10} />}
              {booking.status === 'Cancel'    && <XCircle size={10} />}
              {booking.status}
            </span>
            {hasConflict && (
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                isBlocked ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
              }`}>
                <AlertTriangle size={10} />
                {isBlocked ? 'Blocked' : 'Conflict'}
              </span>
            )}
            {seenAt && booking.status === 'Pending' && !booking.accepted_by && (
              <CountdownBadge seenAt={seenAt} timeSlotEndTime={booking.beTime} />
            )}
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <User size={12} />
            <span>{booking.bookingSno}</span>
            <span>•</span>
            <Calendar size={12} />
            <span>{normalizeDate(booking.bookingDT)}</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 text-xs mb-3">
        {showTonnage && (
          <div>
            <p className="text-gray-500 mb-0.5">Tonnage</p>
            <p className="font-semibold text-gray-900">{booking.tonnage || '—'} tons</p>
          </div>
        )}
        <div>
          <p className="text-gray-500 mb-0.5">Booking Type</p>
          <p className="font-semibold text-gray-900">{booking.BookingStatus || '-'}</p>
        </div>
        <div className="col-span-2">
          <p className="text-gray-500 mb-0.5">Route</p>
          <p className="font-semibold text-gray-900 flex items-center gap-1">
            <MapPin size={12} className="text-gray-400" />
            <span className="truncate">{booking.From || '—'} → {booking.TO || '—'}</span>
          </p>
        </div>
        <div className="col-span-2">
          <p className="text-gray-500 mb-0.5">Time Slot</p>
          <p className="font-semibold text-gray-900 text-xs">
            {booking.bsTime ? new Date(booking.bsTime).toLocaleString() : '—'}
            {' → '}
            {booking.beTime ? new Date(booking.beTime).toLocaleString() : '—'}
          </p>
        </div>
      </div>

      <div className="flex gap-2 pt-3 border-t border-gray-100">
        <button
          onClick={() => onView(booking)}
          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
        >
          <Eye size={14} />
          View Details
        </button>
        {showActions && booking.status === 'Pending' && !booking.accepted_by && (() => {
          const isAccepted = acceptedReqIds.has(cardId)
          const btnDisabled = isBlocked || isAccepted
          return (
          <>
            <button
              onClick={() => !btnDisabled && onAccept(booking)}
              disabled={btnDisabled}
              className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg transition ${
                isAccepted
                  ? 'bg-gray-400 text-white cursor-not-allowed opacity-70'
                  : isBlocked
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'text-white bg-emerald-600 hover:bg-emerald-700'
              }`}
            >
              <CheckCircle size={14} />
              {isAccepted ? 'Accepted' : 'Accept'}
            </button>
            <button
              onClick={() => onCancel(booking.reqId || booking.bId)}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition"
            >
              <XCircle size={14} />
              Cancel
            </button>
          </>
          )
        })()}
      </div>
    </div>
  )
}

// ─── Helper Component ────────────────────────────────────────────────────────
function InfoField({ label, value, badge }) {
  return (
    <div className="bg-gray-50 rounded-lg p-3">
      <label className="text-xs text-gray-500 block mb-1">{label}</label>
      {badge ? (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${badge}`}>
          {value}
        </span>
      ) : (
        <p className="text-sm font-medium text-gray-900 break-words">{value || '-'}</p>
      )}
    </div>
  )
}

// ─── Filter Components ───────────────────────────────────────────────────────
function FilterInput({ placeholder, value, onChange, icon: Icon }) {
  return (
    <div className="relative">
      {Icon && <Icon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />}
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full ${Icon ? 'pl-9' : 'pl-3'} pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400 transition bg-white`}
      />
      {value && (
        <X
          size={14}
          className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-gray-400 hover:text-red-500 transition"
          onClick={() => onChange('')}
        />
      )}
    </div>
  )
}

function FilterSelect({ value, onChange, options }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400 transition bg-white"
    >
      <option value="">All Status</option>
      {options.map((opt) => (
        <option key={opt} value={opt}>{opt}</option>
      ))}
    </select>
  )
}

function FilterDate({ value, onChange }) {
  return (
    <div className="relative">
      <Calendar size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
      <input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400 transition bg-white"
      />
      {value && (
        <X
          size={14}
          className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-gray-400 hover:text-red-500 transition"
          onClick={() => onChange('')}
        />
      )}
    </div>
  )
}

// ─── Table Row Component ─────────────────────────────────────────────────────
const TableRow = ({
  booking, onView, onAccept, onCancel,
  statusColor, normalizeDate,
  conflictIds, acceptedReqIds,
  showActions, showTonnage, readOnly,
  seenAt,
}) => {
  const id          = String(booking.reqId || booking.bId)
  const hasConflict = conflictIds.has(id)
  const isBlocked   = hasConflict && acceptedReqIds.size > 0 && !acceptedReqIds.has(id)
  const isExpired = isTimeSlotExpired(booking)

  return (
    <tr className={`hover:bg-gray-50 transition ${
      hasConflict
        ? (isBlocked ? 'bg-red-50/30' : 'bg-yellow-50/30')
        : seenAt ? 'bg-orange-50/20' : ''
    }`}>
      <td className="py-3 px-4">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-gray-900">#{booking.reqId || booking.bId}</span>
          {hasConflict && (
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium ${isBlocked ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
              <AlertTriangle size={10} />
              {isBlocked ? 'Blocked' : 'Conflict'}
            </span>
          )}
          {isExpired && booking.status === 'Pending' && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
              <AlertTriangle size={10} />
              Expired
            </span>
          )}
          {seenAt && booking.status === 'Pending' && !booking.accepted_by && !isExpired && (
            <CountdownBadge seenAt={seenAt} timeSlotEndTime={booking.beTime} />
          )}
        </div>
      </td>
      <td className="py-3 px-4 text-sm text-gray-600 font-medium">{booking.bookingSno}</td>
      {showTonnage && (
        <td className="py-3 px-4">
          <span className="inline-flex px-2 py-1 text-xs font-semibold bg-orange-100 text-orange-700 rounded-lg">
            {booking.tonnage || '—'} tons
          </span>
        </td>
      )}
      <td className="py-3 px-4">
        <div className="flex items-center gap-1.5 text-sm text-gray-600">
          <MapPin size={14} className="text-gray-400 shrink-0" />
          <span className="truncate max-w-[180px]">{booking.From || '—'} → {booking.TO || '—'}</span>
        </div>
      </td>
      <td className="py-3 px-4 text-sm text-gray-600 whitespace-nowrap">
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-1">
            <Clock size={12} className="text-gray-400" />
            <span className="text-xs">
              {booking.bsTime ? new Date(booking.bsTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}
              {' → '}
              {booking.beTime ? new Date(booking.beTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}
            </span>
          </div>
        </div>
       </td>
      <td className="py-3 px-4">
        <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-semibold ${statusColor(booking.status)}`}>
          {booking.status === 'Pending'   && <Clock size={10} />}
          {booking.status === 'Active'    && <CheckCircle size={10} />}
          {booking.status === 'Completed' && <Check size={10} />}
          {booking.status === 'Cancel'    && <XCircle size={10} />}
          {booking.status}
        </span>
      </td>
      <td className="py-3 px-4 text-sm text-gray-500">
        <span className="inline-flex items-center gap-1">
          {booking.BookingStatus === 'Real-time Booking' ? <Zap size={12} /> : <CalendarDays size={12} />}
          {booking.BookingStatus || '-'}
        </span>
      </td>
      <td className="py-3 px-4 text-sm text-gray-500">{normalizeDate(booking.bookingDT)}</td>
      <td className="py-3 px-4">
        <div className="flex gap-2">
          <button
            onClick={() => onView(booking)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
          >
            <Eye size={12} /> View
          </button>
          {showActions && !readOnly && booking.status === 'Pending' && !booking.accepted_by && !isExpired && (() => {
            const isAccepted = acceptedReqIds.has(id)
            const btnDisabled = isBlocked || isAccepted
            return (
            <>
              <button
                onClick={() => !btnDisabled && onAccept(booking)}
                disabled={btnDisabled}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition ${
                  isAccepted
                    ? 'text-white bg-gray-400 cursor-not-allowed opacity-70'
                    : isBlocked
                    ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                    : 'text-white bg-emerald-600 hover:bg-emerald-700'
                }`}
              >
                <CheckCircle size={12} /> {isAccepted ? 'Accepted' : 'Accept'}
              </button>
              <button
                onClick={() => onCancel(booking.reqId || booking.bId)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition"
              >
                <XCircle size={12} /> Cancel
              </button>
            </>
            )
          })()}
        </div>
      </td>
    </tr>
  )
}

// ─── Reusable Booking Section ─────────────────────────────────────────────────
function BookingSection({
  isLoading, error, filteredData, viewMode, setViewMode,
  currentPage, setCurrentPage, rowsPerPage,
  conflictIds, acceptedReqIds,
  onView, onAccept, onCancel,
  statusColor, normalizeDate,
  showActions, showTonnage, readOnly,
  reqIdLabel,
  filterBar,
  getSeenAt,
}) {
  const totalPagesCount = Math.max(1, Math.ceil(filteredData.length / rowsPerPage))
  const pageData = filteredData.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)

  return (
    <>
      {filterBar}

      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-gray-500">
          {filteredData.length} booking{filteredData.length !== 1 ? 's' : ''} found
        </p>
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
          <button
            onClick={() => setViewMode('table')}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${
              viewMode === 'table' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Table size={14} className="inline mr-1.5" />Table View
          </button>
          <button
            onClick={() => setViewMode('card')}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${
              viewMode === 'card' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Grid size={14} className="inline mr-1.5" />Card View
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw size={20} className="text-gray-400 animate-spin" />
            <span className="ml-3 text-sm text-gray-500">Loading bookings...</span>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 text-sm rounded-lg">
              <AlertCircle size={16} />{error}
            </div>
          </div>
        ) : filteredData.length === 0 ? (
          <div className="text-center py-12">
            <div className="flex flex-col items-center gap-3">
              <div className="p-3 bg-gray-100 rounded-full">
                <ClipboardList size={32} className="text-gray-300" />
              </div>
              <p className="text-sm text-gray-500">No booking requests found</p>
            </div>
          </div>
        ) : (
          <>
            <div className={`${viewMode === 'table' ? 'block' : 'hidden'} overflow-x-auto`}>
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">{reqIdLabel || 'Req ID'}</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Booked By</th>
                    {showTonnage && <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Tonnage</th>}
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Route</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Time Slot</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {pageData.map((b) => (
                    <TableRow
                      key={b.reqId || b.bId}
                      booking={b}
                      onView={onView}
                      onAccept={onAccept}
                      onCancel={onCancel}
                      statusColor={statusColor}
                      normalizeDate={normalizeDate}
                      conflictIds={conflictIds}
                      acceptedReqIds={acceptedReqIds}
                      showActions={showActions}
                      showTonnage={showTonnage}
                      readOnly={readOnly}
                      seenAt={getSeenAt ? getSeenAt(b) : null}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            <div className={`${viewMode === 'card' ? 'block' : 'hidden'} p-4 space-y-3`}>
              {pageData.map((b) => {
                const id          = String(b.reqId || b.bId)
                const hasConflict = conflictIds.has(id)
                const isBlocked   = hasConflict && acceptedReqIds.size > 0 && !acceptedReqIds.has(id)
                return (
                  <BookingCard
                    key={id}
                    booking={b}
                    onView={onView}
                    onAccept={onAccept}
                    onCancel={onCancel}
                    statusColor={statusColor}
                    normalizeDate={normalizeDate}
                    hasConflict={hasConflict}
                    isBlocked={isBlocked}
                    showActions={showActions}
                    showTonnage={showTonnage}
                    seenAt={getSeenAt ? getSeenAt(b) : null}
                    acceptedReqIds={acceptedReqIds}
                  />
                )
              })}
            </div>

            {filteredData.length > rowsPerPage && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50/50">
                <p className="text-sm text-gray-500">
                  Showing{' '}
                  <span className="font-medium text-gray-700">{(currentPage - 1) * rowsPerPage + 1}</span>
                  {' '}to{' '}
                  <span className="font-medium text-gray-700">{Math.min(currentPage * rowsPerPage, filteredData.length)}</span>
                  {' '}of{' '}
                  <span className="font-medium text-gray-700">{filteredData.length}</span> results
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    <ChevronLeft size={14} />Previous
                  </button>
                  <span className="px-3 py-1.5 text-sm text-gray-600">
                    Page {currentPage} of {totalPagesCount}
                  </span>
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPagesCount, p + 1))}
                    disabled={currentPage === totalPagesCount}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    Next<ChevronRight size={14} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  )
}

// ─── Main Component ──────────────────────────────────────────────────────────
export default function AdminBooking() {
  const dispatch    = useDispatch()
  const { authKey } = useSelector((state) => state.auth)

  const { bookingRequests, loading: requestsLoading, error: requestsError } = useSelector((s) => s.adminBookingRequests)
  const { nearest, ranked, loading: findingNearest, error: findError }      = useSelector((s) => s.adminFindNearestDriver)
  const { loading: accepting, success: acceptSuccess, error: acceptError }  = useSelector((s) => s.adminAcceptBooking)

  // UI state
  const [selectedBooking, setSelectedBooking] = useState(null)
  const [acceptModal,     setAcceptModal]     = useState(null)
  const [selectedVehicle, setSelectedVehicle] = useState(null)
  const [viewMode,        setViewMode]        = useState('table')
  const [activeTab,       setActiveTab]       = useState('unplanned')
  const [acceptedReqIds,  setAcceptedReqIds]  = useState(() => loadAcceptedIds())

  // Supervisor cancel notifications
  const [cancelNotifications, setCancelNotifications] = useState([])

  // Data state
  const [unplannedBookings,        setUnplannedBookings]        = useState([])
  const [acceptedScheduleBookings, setAcceptedScheduleBookings] = useState([])
  const [loadingUnplanned,         setLoadingUnplanned]         = useState(false)
  const [loadingAcceptedSchedule,  setLoadingAcceptedSchedule]  = useState(false)

  // Filters — Unplanned
  const [unplannedReqId,     setUnplannedReqId]     = useState('')
  const [unplannedVehicleId, setUnplannedVehicleId] = useState('')
  const [unplannedDriverNo,  setUnplannedDriverNo]  = useState('')
  const [unplannedStatus,    setUnplannedStatus]    = useState('')
  const [unplannedDate,      setUnplannedDate]      = useState('')
  const [unplannedTonnage,   setUnplannedTonnage]   = useState('')

  // Filters — Scheduled
  const [scheduledReqId,     setScheduledReqId]     = useState('')
  const [scheduledVehicleId, setScheduledVehicleId] = useState('')
  const [scheduledDriverNo,  setScheduledDriverNo]  = useState('')
  const [scheduledStatus,    setScheduledStatus]    = useState('')
  const [scheduledDate,      setScheduledDate]      = useState('')
  const [scheduledTonnage,   setScheduledTonnage]   = useState('')

  // Filters — Accepted Schedule
  const [accSchedReqId,     setAccSchedReqId]     = useState('')
  const [accSchedVehicleId, setAccSchedVehicleId] = useState('')
  const [accSchedDriverNo,  setAccSchedDriverNo]  = useState('')
  const [accSchedStatus,    setAccSchedStatus]    = useState('')
  const [accSchedDate,      setAccSchedDate]      = useState('')
  const [accSchedTonnage,   setAccSchedTonnage]   = useState('')

  // Filtered results
  const [unplannedFiltered, setUnplannedFiltered] = useState([])
  const [scheduledFiltered, setScheduledFiltered] = useState([])
  const [accSchedFiltered,  setAccSchedFiltered]  = useState([])

  // Pagination
  const [unplannedCurrentPage, setUnplannedCurrentPage] = useState(1)
  const [scheduledCurrentPage, setScheduledCurrentPage] = useState(1)
  const [accSchedCurrentPage,  setAccSchedCurrentPage]  = useState(1)
  const rowsPerPage = 10

  // ─── 3-min timer tracking ─────────────────────────────────────────────────
  const bookingSeenAtRef = useRef(loadSeenAt())
  const autoTimersRef    = useRef({})
  const processingRef    = useRef(new Set())
  const latestNearestRef = useRef(null)

  // WebSocket
  const wsRef           = useRef(null)
  const lastWsFetchRef  = useRef(0)
  const wsFetchTimerRef = useRef(null)
  const BOOKING_WS_URL  = 'wss://aeroviewbackend.dockyardsoftware.com:8095/Booking'

  // ─── isScheduled helper ────────────────────────────────────────────────────
  const isScheduled = useCallback((b) => {
    const bs = (b.BookingStatus || '').toLowerCase()
    return bs.includes('shedule') || bs.includes('schedule') || bs === 's'
  }, [])

  // ─── validateRequestInfo ──────────────────────────────────────────────────
  const validateRequestInfo = useCallback(async (reqId, key) => {
    try {
      const res = await fetch(
        `https://aeroviewbackend.dockyardsoftware.com/BookingRegister/Getrequestinfo?rid=${reqId}`,
        { method: 'GET', headers: { 'Content-Type': 'application/json', 'auth-key': key } }
      )
      return await res.json()
    } catch (err) {
      console.error('Validation API error:', err)
      return false
    }
  }, [])

  // ─── Fetch scheduled bookings (Redux) ─────────────────────────────────────
  const fetchScheduledBookings = useCallback(async () => {
    try { await dispatch(FetchBookingRequests()) } catch (_) {}
  }, [dispatch])

  // ─── Fetch unplanned + accepted-schedule from main fork endpoint ──────────
  const fetchMainForkBookings = useCallback(async () => {
    if (!authKey) return
    setLoadingUnplanned(true)
    setLoadingAcceptedSchedule(true)
    try {
      const response = await fetch(
        'https://aeroviewbackend.dockyardsoftware.com/BookingRegister/GetMainForkBookingDetails',
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', 'auth-key': authKey },
        }
      )
      const data = await response.json()

      if (data.statusCode === 200 && data.resultSet) {
        const all = data.resultSet

        const realTime = all.filter((b) => b.BookingStatus === 'Real-time Booking')
        const enrichedRealTime = await Promise.all(
          realTime.map(async (booking) => {
            let tonnage = ''
            if (booking.vId) {
              try {
                const vRes = await fetch(
                  `https://aeroviewbackend.dockyardsoftware.com/VehicleDetails/GetVehicleDetails?vehicleId=${encodeURIComponent(booking.vId)}`,
                  { method: 'GET', headers: { 'Content-Type': 'application/json', 'auth-key': authKey } }
                )
                const vData = await vRes.json()
                const vehicle = Array.isArray(vData.resultSet) ? vData.resultSet[0] : vData.resultSet || vData
                tonnage = vehicle?.weightCat || ''
              } catch (_) {}
            }
            return { ...booking, reqId: booking.bId, tonnage }
          })
        )
        setUnplannedBookings(enrichedRealTime)

        const sheduleBookings = all.filter((b) => b.BookingStatus === 'Shedule Booking')
        const enrichedShedule = await Promise.all(
          sheduleBookings.map(async (booking) => {
            let tonnage = booking.tonnage || ''
            if (!tonnage && booking.vId) {
              try {
                const vRes = await fetch(
                  `https://aeroviewbackend.dockyardsoftware.com/VehicleDetails/GetVehicleDetails?vehicleId=${encodeURIComponent(booking.vId)}`,
                  { method: 'GET', headers: { 'Content-Type': 'application/json', 'auth-key': authKey } }
                )
                const vData = await vRes.json()
                const vehicle = Array.isArray(vData.resultSet) ? vData.resultSet[0] : vData.resultSet || vData
                tonnage = vehicle?.weightCat || ''
              } catch (_) {}
            }
            return { ...booking, reqId: booking.bId || booking.reqId, tonnage }
          })
        )
        enrichedShedule.sort((a, b) => new Date(b.bookingDT) - new Date(a.bookingDT))
        setAcceptedScheduleBookings(enrichedShedule)
      } else {
        setUnplannedBookings([])
        setAcceptedScheduleBookings([])
      }
    } catch (error) {
      console.error('Error fetching main fork bookings:', error)
      setUnplannedBookings([])
      setAcceptedScheduleBookings([])
    } finally {
      setLoadingUnplanned(false)
      setLoadingAcceptedSchedule(false)
    }
  }, [authKey])

  // ─── fetchAllBookings (defined before being used) ─────────────────────────
  const fetchAllBookings = useCallback(async () => {
    await Promise.all([fetchMainForkBookings(), fetchScheduledBookings()])
  }, [fetchMainForkBookings, fetchScheduledBookings])

  // ─── WebSocket Helper: Send cancellation notification to employees ────────
  const sendWebSocketCancelNotification = useCallback((bookingId, reason, cancelType = 'system-auto') => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      const cancelMessage = {
        type: 'BOOKING_CANCELLED',
        action: 'cancel',
        bId: bookingId,
        bookingId: bookingId,
        reqId: bookingId,
        status: 'Cancel',
        previousStatus: 'Pending',
        reason: reason,
        cancelType: cancelType,
        timestamp: new Date().toISOString(),
        cancelledBy: cancelType === 'time-slot-expired' ? 'time-slot-expiry' : 'system-auto',
      };
      wsRef.current.send(JSON.stringify(cancelMessage));
      console.log(`[WebSocket] 📢 Sent cancellation notification for booking ${bookingId} (type: ${cancelType})`);
    }
  }, []);

  // ─── Show supervisor cancel notification popup ────────────────────────────
  const showCancelNotification = useCallback((bookingId, reason, cancelType = 'auto-cancel') => {
    const n = {
      id:        `${bookingId}-${Date.now()}`,
      bookingId,
      reason,
      cancelType,
      time:      new Date().toLocaleTimeString(),
    }
    setCancelNotifications(prev => [...prev, n])
    setTimeout(() => {
      setCancelNotifications(prev => prev.filter(x => x.id !== n.id))
    }, 30000)
  }, [])

  const dismissCancelNotification = useCallback((notifId) => {
    setCancelNotifications(prev => prev.filter(n => n.id !== notifId))
  }, [])

  // ─── Core auto-action: runs when 3-min timer fires for a pending booking ──
  const runAutoAction = useCallback(async (booking) => {
    const id = String(booking.reqId || booking.bId)

    if (processingRef.current.has(id)) return
    
    if (isTimeSlotExpired(booking)) {
      console.log(`[3-MIN TIMER] Booking ${id} time slot already expired, cancelling immediately...`);
      processingRef.current.add(id)
      
      const startTimeStr = booking.bsTime ? new Date(booking.bsTime).toLocaleString() : 'N/A';
      const endTimeStr = booking.beTime ? new Date(booking.beTime).toLocaleString() : 'N/A';
      const cancelReason = `Your booking was automatically cancelled because the requested time slot (${startTimeStr} - ${endTimeStr}) has expired before a vehicle could be assigned.`;
      
      try {
        await dispatch(AdminCancelBooking(id));
        await fetchAllBookings();
        showCancelNotification(id, cancelReason, 'time-slot-expired');
        sendWebSocketCancelNotification(id, cancelReason, 'time-slot-expired');
        
        if (bookingSeenAtRef.current[id]) {
          delete bookingSeenAtRef.current[id];
          saveSeenAt(bookingSeenAtRef.current);
        }
      } catch (error) {
        console.error(`[TIME-SLOT EXPIRED ERROR]`, error);
      } finally {
        processingRef.current.delete(id);
      }
      return;
    }

    processingRef.current.add(id)
    console.log(`[3-MIN TIMER FIRED] Booking ${id} — attempting auto-assign...`)

    try {
      dispatch(ResetAdminBookingRequest())
      latestNearestRef.current = null

      await dispatch(AdminFindNearestDriver(booking))

      let bestVehicle = null
      for (let i = 0; i < 15 && !bestVehicle; i++) {
        await new Promise(r => setTimeout(r, 1000))
        if (latestNearestRef.current) bestVehicle = latestNearestRef.current
      }

      if (bestVehicle && !isTimeSlotExpired(booking)) {
        console.log(`[AUTO-ACCEPT] Assigning best vehicle ${bestVehicle.vehicleId} to booking ${id}`)
        await dispatch(AdminAcceptBooking(booking, bestVehicle, authKey))
        await new Promise(r => setTimeout(r, 2000))
        const isValid = await validateRequestInfo(id, authKey)
        if (isValid) {
          setAcceptedReqIds(prev => {
            const next = new Set([...prev, id])
            saveAcceptedIds(next)
            return next
          })
          await fetchAllBookings()
          console.log(`[AUTO-ACCEPT] ✅ Booking ${id} auto-accepted → vehicle ${bestVehicle.vehicleId}`)
        } else {
          console.warn(`[AUTO-ACCEPT] Validation failed for booking ${id}`)
          processingRef.current.delete(id)
        }
      } else if (bestVehicle && isTimeSlotExpired(booking)) {
        const startTimeStr = booking.bsTime ? new Date(booking.bsTime).toLocaleString() : 'N/A';
        const endTimeStr = booking.beTime ? new Date(booking.beTime).toLocaleString() : 'N/A';
        const cancelReason = `Your booking was cancelled because the time slot (${startTimeStr} - ${endTimeStr}) expired while the system was searching for a vehicle.`;
        
        await dispatch(AdminCancelBooking(id))
        await fetchAllBookings()
        showCancelNotification(id, cancelReason, 'time-slot-expired')
        sendWebSocketCancelNotification(id, cancelReason, 'time-slot-expired')
        
        if (bookingSeenAtRef.current[id]) {
          delete bookingSeenAtRef.current[id];
          saveSeenAt(bookingSeenAtRef.current);
        }
      } else {
        const cancelReason = `No suitable vehicle was available within the 3-minute response window. The system searched for a matching vehicle but none were free or matched the required specifications. Please create a new booking.`
        
        console.log(`[AUTO-CANCEL] No vehicle available for booking ${id} — cancelling...`)
        await dispatch(AdminCancelBooking(id))
        await fetchAllBookings()
        
        showCancelNotification(id, cancelReason, 'no-vehicle')
        sendWebSocketCancelNotification(id, cancelReason, 'no-vehicle')
        
        console.log(`[AUTO-CANCEL] ✅ Booking ${id} cancelled — notifications sent`)
      }
    } catch (error) {
      console.error(`[AUTO-ACTION ERROR] Booking ${id}:`, error.message)
      processingRef.current.delete(id)
      try {
        await dispatch(AdminCancelBooking(id))
        await fetchAllBookings()
        const errorReason = `The booking was automatically cancelled after 3 minutes. A system error occurred while searching for an available vehicle: "${error.message}". Please create a new booking.`
        showCancelNotification(id, errorReason, 'system-error')
        sendWebSocketCancelNotification(id, errorReason, 'system-error')
      } catch (_) {}
    } finally {
      processingRef.current.delete(id)
    }
  }, [dispatch, authKey, fetchAllBookings, showCancelNotification, sendWebSocketCancelNotification, validateRequestInfo])

  // ─── Check and handle expired time slots ───────────────────────────────────
  const checkAndHandleExpiredTimeSlots = useCallback(async () => {
    const allPending = [
      ...unplannedBookings.filter(b => b.status === 'Pending' && !b.accepted_by),
      ...(Array.isArray(bookingRequests)
        ? bookingRequests.filter(b => isScheduled(b) && b.status === 'Pending' && !b.accepted_by)
        : []),
    ];

    for (const booking of allPending) {
      const id = String(booking.reqId || booking.bId);
      
      if (processingRef.current.has(id)) continue;
      
      if (isTimeSlotExpired(booking)) {
        console.log(`[TIME-SLOT EXPIRED] Booking ${id} time slot has expired, cancelling...`);
        
        processingRef.current.add(id);
        
        if (autoTimersRef.current[id]) {
          clearTimeout(autoTimersRef.current[id]);
          delete autoTimersRef.current[id];
        }
        
        const startTimeStr = booking.bsTime ? new Date(booking.bsTime).toLocaleString() : 'N/A';
        const endTimeStr = booking.beTime ? new Date(booking.beTime).toLocaleString() : 'N/A';
        const cancelReason = `Your booking was automatically cancelled because the requested time slot (${startTimeStr} - ${endTimeStr}) has expired. No vehicle was assigned before the scheduled end time. Please create a new booking with a valid time slot.`;
        
        try {
          await dispatch(AdminCancelBooking(id));
          await fetchAllBookings();
          
          showCancelNotification(id, cancelReason, 'time-slot-expired');
          sendWebSocketCancelNotification(id, cancelReason, 'time-slot-expired');
          
          if (bookingSeenAtRef.current[id]) {
            delete bookingSeenAtRef.current[id];
            saveSeenAt(bookingSeenAtRef.current);
          }
          
          console.log(`[TIME-SLOT EXPIRED] ✅ Booking ${id} cancelled due to time slot expiry`);
        } catch (error) {
          console.error(`[TIME-SLOT EXPIRED ERROR] Booking ${id}:`, error);
          processingRef.current.delete(id);
        }
      }
    }
  }, [unplannedBookings, bookingRequests, isScheduled, dispatch, fetchAllBookings, showCancelNotification, sendWebSocketCancelNotification]);

  // Keep latest nearest vehicle in sync from Redux
  useEffect(() => {
    if (nearest) latestNearestRef.current = nearest
  }, [nearest])

  // Detect screen size for default view
  useEffect(() => {
    const handleResize = () => setViewMode(window.innerWidth < 768 ? 'card' : 'table')
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Initial data fetch
  useEffect(() => {
    if (authKey) fetchAllBookings()
  }, [authKey, fetchAllBookings])

  // Periodic check for expired time slots (every 30 seconds)
  useEffect(() => {
    if (!authKey) return;
    
    const intervalId = setInterval(() => {
      checkAndHandleExpiredTimeSlots();
    }, 30000);
    
    return () => clearInterval(intervalId);
  }, [authKey, checkAndHandleExpiredTimeSlots]);

  // WebSocket connection
  useEffect(() => {
    if (!authKey) return
    if (wsRef.current) wsRef.current.close()
    wsRef.current = new WebSocket(`${BOOKING_WS_URL}?token=${encodeURIComponent(authKey)}`)
    wsRef.current.onopen    = () => console.log('✅ Booking WebSocket Connected')
    wsRef.current.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data)
        if (message.type || message.action || message.bId || message.bookingId) {
          const now    = Date.now()
          const minGap = 5000
          if (now - lastWsFetchRef.current >= minGap) {
            lastWsFetchRef.current = now
            fetchAllBookings()
          } else if (!wsFetchTimerRef.current) {
            const delay = minGap - (now - lastWsFetchRef.current)
            wsFetchTimerRef.current = setTimeout(() => {
              lastWsFetchRef.current  = Date.now()
              wsFetchTimerRef.current = null
              fetchAllBookings()
            }, delay)
          }
        }
      } catch (_) {}
    }
    wsRef.current.onerror = (e) => console.error('WebSocket Error:', e)
    wsRef.current.onclose = ()  => console.log('WebSocket Disconnected')
    return () => {
      wsRef.current?.close()
      wsRef.current = null
      if (wsFetchTimerRef.current) { clearTimeout(wsFetchTimerRef.current); wsFetchTimerRef.current = null }
    }
  }, [authKey, fetchAllBookings])

  // ─── Conflict detection
  const unplannedConflictIds = useMemo(() =>
    getConflictingReqIds(unplannedBookings.filter(b => b.status === 'Pending')),
  [unplannedBookings])

  const scheduledConflictIds = useMemo(() => {
    const pending = Array.isArray(bookingRequests)
      ? bookingRequests.filter(b => isScheduled(b) && b.status === 'Pending')
      : []
    return getConflictingReqIds(pending)
  }, [bookingRequests, isScheduled])

  const emptyConflictIds = useMemo(() => new Set(), [])

  // ─── Filter: Unplanned
  useEffect(() => {
    let r = [...unplannedBookings]
    if (unplannedReqId.trim())     r = r.filter(b => String(b.reqId || b.bId || '').includes(unplannedReqId.trim()))
    if (unplannedVehicleId.trim()) r = r.filter(b => (b.vId   || '').toLowerCase().includes(unplannedVehicleId.trim().toLowerCase()))
    if (unplannedDriverNo.trim())  r = r.filter(b => (b.dSNo  || '').toLowerCase().includes(unplannedDriverNo.trim().toLowerCase()))
    if (unplannedTonnage.trim())   r = r.filter(b => (b.tonnage || '').toLowerCase().includes(unplannedTonnage.trim().toLowerCase()))
    if (unplannedStatus)           r = r.filter(b => (b.status || '') === unplannedStatus)
    if (unplannedDate)             r = r.filter(b => normalizeDate(b.bookingDT) === unplannedDate)
    setUnplannedFiltered(r)
    setUnplannedCurrentPage(1)
  }, [unplannedReqId, unplannedVehicleId, unplannedDriverNo, unplannedTonnage, unplannedStatus, unplannedDate, unplannedBookings])

  // ─── Filter: Scheduled
  useEffect(() => {
    let r = Array.isArray(bookingRequests) ? bookingRequests.filter(b => isScheduled(b)) : []
    if (scheduledReqId.trim())     r = r.filter(b => String(b.reqId || b.bId || '').includes(scheduledReqId.trim()))
    if (scheduledVehicleId.trim()) r = r.filter(b => (b.vId   || '').toLowerCase().includes(scheduledVehicleId.trim().toLowerCase()))
    if (scheduledDriverNo.trim())  r = r.filter(b => (b.dSNo  || '').toLowerCase().includes(scheduledDriverNo.trim().toLowerCase()))
    if (scheduledTonnage.trim())   r = r.filter(b => (b.tonnage || '').toLowerCase().includes(scheduledTonnage.trim().toLowerCase()))
    if (scheduledStatus)           r = r.filter(b => (b.status || '') === scheduledStatus)
    if (scheduledDate)             r = r.filter(b => normalizeDate(b.bookingDT) === scheduledDate)
    r.sort((a, b) => new Date(b.bookingDT) - new Date(a.bookingDT))
    setScheduledFiltered(r)
    setScheduledCurrentPage(1)
  }, [scheduledReqId, scheduledVehicleId, scheduledDriverNo, scheduledTonnage, scheduledStatus, scheduledDate, bookingRequests, isScheduled])

  // ─── Filter: Accepted Schedule
  useEffect(() => {
    let r = [...acceptedScheduleBookings]
    if (accSchedReqId.trim())     r = r.filter(b => String(b.bId || b.reqId || '').includes(accSchedReqId.trim()))
    if (accSchedVehicleId.trim()) r = r.filter(b => (b.vId   || '').toLowerCase().includes(accSchedVehicleId.trim().toLowerCase()))
    if (accSchedDriverNo.trim())  r = r.filter(b => (b.dSNo  || '').toLowerCase().includes(accSchedDriverNo.trim().toLowerCase()))
    if (accSchedTonnage.trim())   r = r.filter(b => (b.tonnage || '').toLowerCase().includes(accSchedTonnage.trim().toLowerCase()))
    if (accSchedStatus)           r = r.filter(b => (b.status || '') === accSchedStatus)
    if (accSchedDate)             r = r.filter(b => normalizeDate(b.bookingDT) === accSchedDate)
    setAccSchedFiltered(r)
    setAccSchedCurrentPage(1)
  }, [accSchedReqId, accSchedVehicleId, accSchedDriverNo, accSchedTonnage, accSchedStatus, accSchedDate, acceptedScheduleBookings])

  useEffect(() => { if (nearest) setSelectedVehicle(nearest) }, [nearest])

  // ─── Register 3-min countdown timers for newly-seen pending bookings ──────
  useEffect(() => {
    const dataLoaded = !loadingUnplanned && !requestsLoading

    const allPending = [
      ...unplannedBookings.filter(b => b.status === 'Pending' && !b.accepted_by),
      ...(Array.isArray(bookingRequests)
        ? bookingRequests.filter(b => isScheduled(b) && b.status === 'Pending' && !b.accepted_by)
        : []),
    ]

    allPending.forEach((booking) => {
      const id = String(booking.reqId || booking.bId)

      if (processingRef.current.has(id)) return
      
      if (isTimeSlotExpired(booking)) {
        console.log(`[TIME-SLOT CHECK] Booking ${id} time slot expired, cancelling immediately`);
        (async () => {
          processingRef.current.add(id);
          const startTimeStr = booking.bsTime ? new Date(booking.bsTime).toLocaleString() : 'N/A';
          const endTimeStr = booking.beTime ? new Date(booking.beTime).toLocaleString() : 'N/A';
          const cancelReason = `Your booking was automatically cancelled because the requested time slot (${startTimeStr} - ${endTimeStr}) has expired.`;
          try {
            await dispatch(AdminCancelBooking(id));
            await fetchAllBookings();
            showCancelNotification(id, cancelReason, 'time-slot-expired');
            sendWebSocketCancelNotification(id, cancelReason, 'time-slot-expired');
            if (bookingSeenAtRef.current[id]) {
              delete bookingSeenAtRef.current[id];
              saveSeenAt(bookingSeenAtRef.current);
            }
          } catch (error) {
            console.error(`[EXPIRED CANCEL ERROR]`, error);
          } finally {
            processingRef.current.delete(id);
          }
        })();
        return;
      }

      const timeSlotRemaining = getTimeSlotRemaining(booking);
      if (timeSlotRemaining && timeSlotRemaining < 60000) {
        console.log(`[TIME-SLOT WARNING] Booking ${id} has only ${Math.round(timeSlotRemaining/1000)}s remaining, cancelling immediately`);
        (async () => {
          processingRef.current.add(id);
          const startTimeStr = booking.bsTime ? new Date(booking.bsTime).toLocaleString() : 'N/A';
          const endTimeStr = booking.beTime ? new Date(booking.beTime).toLocaleString() : 'N/A';
          const cancelReason = `Your booking was automatically cancelled because the time slot (${startTimeStr} - ${endTimeStr}) has less than 1 minute remaining and no vehicle could be assigned in time.`;
          try {
            await dispatch(AdminCancelBooking(id));
            await fetchAllBookings();
            showCancelNotification(id, cancelReason, 'time-slot-expiring');
            sendWebSocketCancelNotification(id, cancelReason, 'time-slot-expiring');
          } catch (error) {
            console.error(error);
          } finally {
            processingRef.current.delete(id);
          }
        })();
        return;
      }

      if (autoTimersRef.current[id] !== undefined) return

      if (!bookingSeenAtRef.current[id]) {
        bookingSeenAtRef.current[id] = Date.now()
        saveSeenAt(bookingSeenAtRef.current)
        console.log('[3-MIN TIMER] New pending booking seen:', id)
      } else {
        console.log('[3-MIN TIMER] Restored timer for booking after refresh:', id)
      }

      const elapsed = Date.now() - bookingSeenAtRef.current[id]
      const maxWaitTime = 3 * 60 * 1000;
      const effectiveWaitTime = Math.min(maxWaitTime, timeSlotRemaining || maxWaitTime);
      const remaining = Math.max(0, effectiveWaitTime - elapsed)

      if (remaining === 0) {
        console.log('[3-MIN TIMER] Booking past deadline, firing now:', id)
        runAutoAction(booking)
      } else {
        console.log('[3-MIN TIMER] Scheduling', Math.round(remaining / 1000), 's for booking', id)
        autoTimersRef.current[id] = setTimeout(() => {
          delete autoTimersRef.current[id]
          if (!processingRef.current.has(id)) {
            runAutoAction(booking)
          }
        }, remaining)
      }
    })

    if (!dataLoaded) return

    const pendingIds = new Set(allPending.map(b => String(b.reqId || b.bId)))

    let seenAtChanged = false
    Object.keys(bookingSeenAtRef.current).forEach(id => {
      if (!pendingIds.has(id)) {
        delete bookingSeenAtRef.current[id]
        if (autoTimersRef.current[id]) {
          clearTimeout(autoTimersRef.current[id])
          delete autoTimersRef.current[id]
        }
        seenAtChanged = true
      }
    })
    if (seenAtChanged) saveSeenAt(bookingSeenAtRef.current)

    setAcceptedReqIds(prev => {
      const pruned = new Set([...prev].filter(id => pendingIds.has(id)))
      if (pruned.size !== prev.size) { saveAcceptedIds(pruned); return pruned }
      return prev
    })
  }, [unplannedBookings, bookingRequests, runAutoAction, loadingUnplanned, requestsLoading, dispatch, fetchAllBookings, showCancelNotification, sendWebSocketCancelNotification, isScheduled])

  // Cleanup all timers on unmount
  useEffect(() => {
    return () => {
      Object.values(autoTimersRef.current).forEach(t => t && clearTimeout(t))
      autoTimersRef.current = {}
    }
  }, [])

  // ─── Manual Accept: cancel any pending timer first ────────────────────────
  const handleOpenAcceptModal = async (booking) => {
    const id = String(booking.reqId || booking.bId)
    if (autoTimersRef.current[id]) {
      clearTimeout(autoTimersRef.current[id])
      delete autoTimersRef.current[id]
    }
    processingRef.current.add(id)

    dispatch(ResetAdminBookingRequest())
    setSelectedVehicle(null)
    setAcceptModal(booking)
    try { await dispatch(AdminFindNearestDriver(booking)) } catch (_) {}
  }

  const handleCancelBooking = async (reqId) => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) return
    const id = String(reqId)
    if (autoTimersRef.current[id]) {
      clearTimeout(autoTimersRef.current[id])
      delete autoTimersRef.current[id]
    }
    processingRef.current.add(id)
    try {
      await dispatch(AdminCancelBooking(reqId))
      await fetchAllBookings()
      
      const cancelReason = "Your booking was cancelled by the administrator. Please contact support if you need to reschedule."
      sendWebSocketCancelNotification(id, cancelReason, 'admin-manual')
      showCancelNotification(id, cancelReason, 'admin-manual')
      
    } catch (err) {
      console.error(err)
      alert('❌ Cancel failed')
    } finally {
      processingRef.current.delete(id)
    }
  }

  // ─── Manual Accept Modal handlers
  const handleConfirmAccept = async () => {
    if (!acceptModal || !selectedVehicle) return
    try {
      await dispatch(AdminAcceptBooking(acceptModal, selectedVehicle, authKey))
      const reqId   = acceptModal.reqId || acceptModal.bId
      const isValid = await validateRequestInfo(reqId, authKey)
      if (!isValid) { alert('❌ Request validation failed.'); return }
      setAcceptedReqIds(prev => {
        const next = new Set([...prev, String(reqId)])
        saveAcceptedIds(next)
        return next
      })
      await fetchAllBookings()
      setAcceptModal(null)
      dispatch(ResetAdminBookingRequest())
    } catch (error) {
      console.error(error)
      alert('❌ Something went wrong')
    }
  }

  const handleCloseAcceptModal = () => {
    setAcceptModal(null)
    setSelectedVehicle(null)
    dispatch(ResetAdminBookingRequest())
  }

  // ─── seenAt getter for badge display in table/card rows
  const getSeenAt = useCallback((booking) => {
    const id = String(booking.reqId || booking.bId)
    return bookingSeenAtRef.current[id] || null
  }, [])

  // ─── Stats
  const scheduledList  = Array.isArray(bookingRequests) ? bookingRequests.filter(b => isScheduled(b)) : []
  const allBookings    = [...unplannedBookings, ...scheduledList]
  const totalCount     = unplannedBookings.length + scheduledList.length
  const pendingCount   = allBookings.filter(b => b.status === 'Pending').length
  const acceptedCount  = allBookings.filter(b => b.status === 'Active' || b.status === 'Accepted').length

  const statusColor = (s) => {
    switch (s) {
      case 'Active':      return 'bg-emerald-50 text-emerald-700'
      case 'Pending':     return 'bg-yellow-50 text-yellow-700'
      case 'Completed':   return 'bg-blue-50 text-blue-700'
      case 'Enroute':     return 'bg-purple-50 text-purple-700'
      case 'In Progress': return 'bg-indigo-50 text-indigo-700'
      case 'Cancel':      return 'bg-red-50 text-red-700'
      default:            return 'bg-gray-50 text-gray-700'
    }
  }

  const statusOptions = ['Pending', 'Active', 'Completed', 'Enroute', 'In Progress', 'Cancel']
  const isLoadingAny  = loadingUnplanned || loadingAcceptedSchedule || requestsLoading

  const buildFilterBar = ({ totalLabel, count, filters, hasActive, onClear }) => (
    <div className="bg-white rounded-xl border border-gray-100 p-4 mb-5 shadow-sm">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
        {filters}
      </div>
      {hasActive && (
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
          <p className="text-sm text-gray-500">
            Showing <span className="font-semibold text-gray-700">{count}</span> {totalLabel}
          </p>
          <button
            onClick={onClear}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
          >
            <X size={12} />Clear all filters
          </button>
        </div>
      )}
    </div>
  )

  return (
    <AdminLayout>
      <CancelNotificationPopup
        notifications={cancelNotifications}
        onDismiss={dismissCancelNotification}
      />

      {/* WebSocket Status Indicator */}
      <div className="flex items-center justify-end mb-3">
        {/* <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-full">
          <div className={`w-2 h-2 rounded-full ${wsRef.current?.readyState === WebSocket.OPEN ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></div>
          <span className="text-xs text-gray-600">
            {wsRef.current?.readyState === WebSocket.OPEN ? '📡 Live Updates Active' : '🔌 Connecting...'}
          </span>
          {wsRef.current?.readyState === WebSocket.OPEN && (
            <span className="text-xs text-green-600 font-medium">Employees will receive cancellation notifications</span>
          )}
        </div> */}
      </div>

      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-xl">
                <ClipboardList size={22} className="text-blue-600" />
              </div>
              Booking Management
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Manage and track all booking requests with real-time updates
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 rounded-full border border-emerald-200">
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
              <span className="text-xs font-medium text-emerald-700">Live Updates Active</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-orange-50 rounded-full border border-orange-200">
              <Clock size={12} className="text-orange-600" />
              <span className="text-xs font-medium text-orange-700">3-Min Auto-Action</span>
            </div> 
            <button
              onClick={fetchAllBookings}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition"
              title="Refresh"
            >
              <RefreshCw size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <StatCard title="Total Requests"   value={totalCount}                       icon={ClipboardList}  color="blue"   />
        <StatCard title="Unplanned"        value={unplannedBookings.length}         icon={Zap}            color="purple" />
        <StatCard title="Scheduled"        value={scheduledList.length}             icon={CalendarDays}   color="indigo" />
        <StatCard title="Acc. Schedule"    value={acceptedScheduleBookings.length}  icon={CalendarCheck}  color="teal"   />
        <StatCard title="Pending"          value={pendingCount}                     icon={Clock}          color="yellow" />
        <StatCard title="Accepted"         value={acceptedCount}                    icon={CheckCircle}    color="green"  />
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-6 mb-5 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
          <span className="text-gray-600">Conflict — Overlapping time slot</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-red-400"></div>
          <span className="text-gray-600">Blocked — Another overlapping booking accepted</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-orange-400"></div>
          <span className="text-gray-600">Countdown — Auto-assign or cancel in 3 min</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-amber-400"></div>
          <span className="text-gray-600">Time Slot Expiry — Auto-cancel when time slot expires</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-emerald-400"></div>
          <span className="text-gray-600">Live — Real-time updates via WebSocket</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-5">
        <TabButton
          active={activeTab === 'unplanned'}
          onClick={() => setActiveTab('unplanned')}
          icon={Zap}
          count={unplannedFiltered.length}
        >
          Unplanned Bookings
        </TabButton>
        <TabButton
          active={activeTab === 'scheduled'}
          onClick={() => setActiveTab('scheduled')}
          icon={CalendarDays}
          count={scheduledFiltered.length}
        >
          Scheduled Bookings
        </TabButton>
        <TabButton
          active={activeTab === 'acceptedSchedule'}
          onClick={() => setActiveTab('acceptedSchedule')}
          icon={CalendarCheck}
          count={accSchedFiltered.length}
          activeColor="teal"
        >
          Accepted Schedule
        </TabButton>
      </div>

      {/* TAB 1: UNPLANNED */}
      {activeTab === 'unplanned' && (
        <BookingSection
          isLoading={isLoadingAny}
          error={null}
          filteredData={unplannedFiltered}
          viewMode={viewMode}
          setViewMode={setViewMode}
          currentPage={unplannedCurrentPage}
          setCurrentPage={setUnplannedCurrentPage}
          rowsPerPage={rowsPerPage}
          conflictIds={unplannedConflictIds}
          acceptedReqIds={acceptedReqIds}
          onView={setSelectedBooking}
          onAccept={handleOpenAcceptModal}
          onCancel={handleCancelBooking}
          statusColor={statusColor}
          normalizeDate={normalizeDate}
          showActions={false}
          showTonnage={false}
          readOnly={false}
          reqIdLabel="Booking ID"
          getSeenAt={getSeenAt}
          filterBar={buildFilterBar({
            totalLabel: 'unplanned requests',
            count: unplannedFiltered.length,
            hasActive: !!(unplannedReqId || unplannedVehicleId || unplannedDriverNo || unplannedTonnage || unplannedStatus || unplannedDate),
            onClear: () => { setUnplannedReqId(''); setUnplannedVehicleId(''); setUnplannedDriverNo(''); setUnplannedTonnage(''); setUnplannedStatus(''); setUnplannedDate('') },
            filters: (
              <>
                <FilterInput  placeholder="Booking ID"  value={unplannedReqId}     onChange={setUnplannedReqId}     icon={Search} />
                <FilterInput  placeholder="Vehicle ID"  value={unplannedVehicleId} onChange={setUnplannedVehicleId} icon={Truck}  />
                <FilterInput  placeholder="Driver No"   value={unplannedDriverNo}  onChange={setUnplannedDriverNo}  icon={User}   />
                <FilterInput  placeholder="Tonnage"     value={unplannedTonnage}   onChange={setUnplannedTonnage}   icon={Weight} />
                <FilterSelect value={unplannedStatus}   onChange={setUnplannedStatus} options={statusOptions} />
                <FilterDate   value={unplannedDate}     onChange={setUnplannedDate} />
              </>
            ),
          })}
        />
      )}

      {/* TAB 2: SCHEDULED */}
      {activeTab === 'scheduled' && (
        <BookingSection
          isLoading={isLoadingAny}
          error={requestsError}
          filteredData={scheduledFiltered}
          viewMode={viewMode}
          setViewMode={setViewMode}
          currentPage={scheduledCurrentPage}
          setCurrentPage={setScheduledCurrentPage}
          rowsPerPage={rowsPerPage}
          conflictIds={scheduledConflictIds}
          acceptedReqIds={acceptedReqIds}
          onView={setSelectedBooking}
          onAccept={handleOpenAcceptModal}
          onCancel={handleCancelBooking}
          statusColor={statusColor}
          normalizeDate={normalizeDate}
          showActions={true}
          showTonnage={true}
          readOnly={false}
          reqIdLabel="Req ID"
          getSeenAt={getSeenAt}
          filterBar={buildFilterBar({
            totalLabel: 'scheduled requests',
            count: scheduledFiltered.length,
            hasActive: !!(scheduledReqId || scheduledVehicleId || scheduledDriverNo || scheduledTonnage || scheduledStatus || scheduledDate),
            onClear: () => { setScheduledReqId(''); setScheduledVehicleId(''); setScheduledDriverNo(''); setScheduledTonnage(''); setScheduledStatus(''); setScheduledDate('') },
            filters: (
              <>
                <FilterInput  placeholder="Request ID"  value={scheduledReqId}     onChange={setScheduledReqId}     icon={Search} />
                <FilterInput  placeholder="Vehicle ID"  value={scheduledVehicleId} onChange={setScheduledVehicleId} icon={Truck}  />
                <FilterInput  placeholder="Driver No"   value={scheduledDriverNo}  onChange={setScheduledDriverNo}  icon={User}   />
                <FilterInput  placeholder="Tonnage"     value={scheduledTonnage}   onChange={setScheduledTonnage}   icon={Weight} />
                <FilterSelect value={scheduledStatus}   onChange={setScheduledStatus} options={statusOptions} />
                <FilterDate   value={scheduledDate}     onChange={setScheduledDate} />
              </>
            ),
          })}
        />
      )}

      {/* TAB 3: ACCEPTED SCHEDULE */}
      {activeTab === 'acceptedSchedule' && (
        <BookingSection
          isLoading={loadingAcceptedSchedule}
          error={null}
          filteredData={accSchedFiltered}
          viewMode={viewMode}
          setViewMode={setViewMode}
          currentPage={accSchedCurrentPage}
          setCurrentPage={setAccSchedCurrentPage}
          rowsPerPage={rowsPerPage}
          conflictIds={emptyConflictIds}
          acceptedReqIds={new Set()}
          onView={setSelectedBooking}
          onAccept={handleOpenAcceptModal}
          onCancel={handleCancelBooking}
          statusColor={statusColor}
          normalizeDate={normalizeDate}
          showActions={false}
          showTonnage={false}
          readOnly={true}
          reqIdLabel="Booking ID"
          filterBar={buildFilterBar({
            totalLabel: 'accepted schedule bookings',
            count: accSchedFiltered.length,
            hasActive: !!(accSchedReqId || accSchedVehicleId || accSchedDriverNo || accSchedTonnage || accSchedStatus || accSchedDate),
            onClear: () => { setAccSchedReqId(''); setAccSchedVehicleId(''); setAccSchedDriverNo(''); setAccSchedTonnage(''); setAccSchedStatus(''); setAccSchedDate('') },
            filters: (
              <>
                <FilterInput  placeholder="Booking ID"  value={accSchedReqId}     onChange={setAccSchedReqId}     icon={Search} />
                <FilterInput  placeholder="Vehicle ID"  value={accSchedVehicleId} onChange={setAccSchedVehicleId} icon={Truck}  />
                <FilterInput  placeholder="Driver No"   value={accSchedDriverNo}  onChange={setAccSchedDriverNo}  icon={User}   />
                <FilterInput  placeholder="Tonnage"     value={accSchedTonnage}   onChange={setAccSchedTonnage}   icon={Weight} />
                <FilterSelect value={accSchedStatus}    onChange={setAccSchedStatus} options={statusOptions} />
                <FilterDate   value={accSchedDate}      onChange={setAccSchedDate} />
              </>
            ),
          })}
        />
      )}

      {/* Booking Detail Modal - Keep existing code */}
      {selectedBooking && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full border border-gray-100 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white z-10 flex items-center justify-between p-5 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-xl">
                  <ClipboardList size={18} className="text-blue-600" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">Booking Details</h2>
              </div>
              <button onClick={() => setSelectedBooking(null)} className="p-2 hover:bg-gray-100 rounded-xl transition">
                <X size={18} className="text-gray-400" />
              </button>
            </div>

            <div className="p-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <InfoField label="Request ID"      value={selectedBooking.reqId || selectedBooking.bId} />
                <InfoField label="Status"          value={selectedBooking.status} badge={statusColor(selectedBooking.status)} />
                <InfoField label="Booked By"       value={selectedBooking.bookingSno} />
                <InfoField label="Tonnage"         value={selectedBooking.tonnage ? `${selectedBooking.tonnage} tons` : '-'} />
                <InfoField label="Vehicle ID"      value={selectedBooking.vId   || '-'} />
                <InfoField label="Driver No"       value={selectedBooking.dSNo  || '-'} />
                <InfoField label="Booking Type"    value={selectedBooking.BookingStatus || '-'} />
                <InfoField label="Booking Date"    value={normalizeDate(selectedBooking.bookingDT)} />
                <InfoField label="From"            value={selectedBooking.From  || '-'} />
                <InfoField label="To"              value={selectedBooking.TO    || '-'} />
                <InfoField label="Start Location"  value={`${selectedBooking.slatitude || '-'}, ${selectedBooking.slongitude || '-'}`} />
                <InfoField label="End Location"    value={`${selectedBooking.elatitude || '-'}, ${selectedBooking.elongitude || '-'}`} />
                <InfoField label="Scheduled Start" value={selectedBooking.bsTime ? new Date(selectedBooking.bsTime).toLocaleString() : '-'} />
                <InfoField label="Scheduled End"   value={selectedBooking.beTime ? new Date(selectedBooking.beTime).toLocaleString() : '-'} />
              </div>
              <div className="mt-3">
                <InfoField label="Remark" value={selectedBooking.Remark || '-'} />
              </div>
            </div>

            <div className="flex gap-3 p-5 border-t border-gray-100 sticky bottom-0 bg-white">
              {selectedBooking.status === 'Pending' && !selectedBooking.accepted_by &&
                selectedBooking.BookingStatus !== 'Shedule Booking' && !isTimeSlotExpired(selectedBooking) && (
                <button
                  onClick={() => { setSelectedBooking(null); handleOpenAcceptModal(selectedBooking) }}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl transition"
                >
                  <CheckCircle size={16} />Accept & Assign Driver
                </button>
              )}
              <button
                onClick={() => setSelectedBooking(null)}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Accept & Assign Modal - Keep existing code */}
      {acceptModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full border border-gray-100 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white z-10 flex items-center justify-between p-5 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-xl">
                  <CheckCircle size={18} className="text-emerald-600" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">Accept & Assign Booking</h2>
              </div>
              <button onClick={handleCloseAcceptModal} className="p-2 hover:bg-gray-100 rounded-xl transition">
                <X size={18} className="text-gray-400" />
              </button>
            </div>

            <div className="p-5">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 mb-5">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <ClipboardList size={14} />Booking Summary
                </h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-gray-500">Request ID:</span><p className="font-semibold text-gray-900 mt-0.5">{acceptModal.reqId || acceptModal.bId}</p></div>
                  <div><span className="text-gray-500">Tonnage:</span><p className="font-semibold text-orange-600 mt-0.5">{acceptModal.tonnage} tons</p></div>
                  <div><span className="text-gray-500">From:</span><p className="font-semibold text-gray-900 mt-0.5">{acceptModal.From || '—'}</p></div>
                  <div><span className="text-gray-500">To:</span><p className="font-semibold text-gray-900 mt-0.5">{acceptModal.TO || '—'}</p></div>
                  <div><span className="text-gray-500">Booked By:</span><p className="font-semibold text-gray-900 mt-0.5">{acceptModal.bookingSno}</p></div>
                  <div><span className="text-gray-500">Scheduled Start:</span><p className="font-semibold text-gray-900 mt-0.5">{acceptModal.bsTime ? new Date(acceptModal.bsTime).toLocaleString() : '-'}</p></div>
                  <div><span className="text-gray-500">Destination Lat:</span><p className="font-semibold text-gray-900 mt-0.5">{acceptModal.elatitude || '-'}</p></div>
                  <div><span className="text-gray-500">Destination Lon:</span><p className="font-semibold text-gray-900 mt-0.5">{acceptModal.elongitude || '-'}</p></div>
                </div>
              </div>

              <div>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Truck size={14} />Select Vehicle to Assign
                </h3>

                {findingNearest && (
                  <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-xl">
                    <RefreshCw size={18} className="text-blue-600 animate-spin" />
                    <span className="text-sm text-blue-700">Finding best matching vehicle...</span>
                  </div>
                )}

                {findError && !findingNearest && (
                  <div className="flex items-start gap-3 p-4 bg-red-50 rounded-xl">
                    <AlertCircle size={18} className="text-red-600 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-red-700">No Matching Vehicle Found</p>
                      <p className="text-xs text-red-600 mt-1">{findError}</p>
                    </div>
                  </div>
                )}

                {ranked.length > 0 && !findingNearest && (
                  <div className="space-y-3 max-h-80 overflow-y-auto pr-2">
                    {ranked.map((v, idx) => {
                      const isSelected = selectedVehicle?.vehicleId === v.vehicleId
                      const isNearest  = idx === 0
                      const isDisabled = !isNearest
                      return (
                        <div
                          key={v.vehicleId}
                          onClick={() => isNearest && setSelectedVehicle(v)}
                          className={`rounded-xl border-2 p-4 transition-all ${
                            isDisabled
                              ? 'opacity-40 cursor-not-allowed bg-gray-50 border-gray-200'
                              : isSelected
                              ? 'cursor-pointer border-emerald-500 bg-emerald-50'
                              : 'cursor-pointer border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-3">
                              {isSelected
                                ? <CheckCircle size={18} className="text-emerald-600" />
                                : <div className={`w-4 h-4 rounded-full border-2 ${isDisabled ? 'border-gray-200' : 'border-gray-300'}`} />
                              }
                              <span className={`text-base font-semibold ${isDisabled ? 'text-gray-400' : 'text-gray-900'}`}>{v.vehicleId}</span>
                              {isNearest && (
                                <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-semibold">
                                  Best Match ✓ Auto-selected
                                </span>
                              )}
                              {isDisabled && (
                                <span className="text-xs bg-gray-100 text-gray-400 px-2 py-0.5 rounded-full">
                                  Not available
                                </span>
                              )}
                            </div>
                            <span className={`text-sm font-semibold ${isDisabled ? 'text-gray-400' : 'text-blue-600'}`}>{v.distanceKm} km</span>
                          </div>
                          <div className="grid grid-cols-3 gap-3 text-sm text-gray-600 ml-7">
                            <div><span className="text-gray-400">Driver:</span><span className={`ml-1 font-medium ${isDisabled ? 'text-gray-400' : ''}`}>{v.driverSno}</span></div>
                            <div><span className="text-gray-400">Weight:</span><span className={`ml-1 font-medium ${isDisabled ? 'text-gray-400' : 'text-orange-600'}`}>{v.weightCat}</span></div>
                            <div><span className="text-gray-400">Type:</span><span className={`ml-1 font-medium ${isDisabled ? 'text-gray-400' : ''}`}>{v.vehicleType}</span></div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>

              {acceptError && (
                <div className="mt-4 p-3 bg-red-50 border border-red-100 rounded-xl">
                  <p className="text-sm text-red-600 flex items-center gap-2">
                    <AlertCircle size={14} />{acceptError}
                  </p>
                </div>
              )}

              {acceptSuccess && (
                <div className="mt-4 p-3 bg-emerald-50 border border-emerald-100 rounded-xl">
                  <p className="text-sm text-emerald-600 flex items-center gap-2">
                    <CheckCircle size={14} />Booking accepted — request sent to driver successfully.
                  </p>
                </div>
              )}
            </div>

            <div className="flex gap-3 p-5 border-t border-gray-100 sticky bottom-0 bg-white">
              <button
                onClick={handleCloseAcceptModal}
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmAccept}
                disabled={!selectedVehicle || findingNearest || accepting}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {accepting ? (
                  <><RefreshCw size={16} className="animate-spin" />Sending to Driver...</>
                ) : (
                  <><CheckCircle size={16} />Accept & Assign {selectedVehicle ? `(${selectedVehicle.vehicleId})` : ''}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  )
}